/**
 * The social networks a footer link can point at, and the icon each one wears.
 *
 * One list for both ends: the admin CMS offers exactly these in its icon picker, and the public
 * footer draws exactly these. The key is what the admin saves in the social link's `icon` field
 * (core's `SocialLink.icon`, free text up to 100 chars). The art is Simple Icons (CC0, white
 * fill), `public/game/ui/social/<key>.svg` — see `public/game/ASSETS.md`.
 */
export const SOCIAL_PLATFORMS = [
  { key: 'discord', label: 'Discord' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitch', label: 'Twitch' },
  { key: 'reddit', label: 'Reddit' },
  { key: 'steam', label: 'Steam' },
  { key: 'vk', label: 'VK' },
  { key: 'threads', label: 'Threads' },
] as const;

export type SocialPlatformKey = (typeof SOCIAL_PLATFORMS)[number]['key'];

/** Names a link's platform is commonly typed as, beyond its own key. */
const ALIASES: Record<string, SocialPlatformKey> = { twitter: 'x', 'x (twitter)': 'x', vkontakte: 'vk', yt: 'youtube' };

/**
 * The icon a saved link should wear: its chosen `icon` key, or — for links saved before the
 * picker existed (the field held an emoji, or nothing) — whatever its platform name matches.
 * `null` when neither names a known network; the footer then shows the platform as text.
 */
export function socialPlatformKey(link: { icon?: string | null; platform?: string | null }): SocialPlatformKey | null {
  for (const raw of [link.icon, link.platform]) {
    const name = (raw ?? '').trim().toLowerCase();
    if (!name) continue;
    const known = SOCIAL_PLATFORMS.find((p) => p.key === name || p.label.toLowerCase() === name);
    if (known) return known.key;
    if (ALIASES[name]) return ALIASES[name];
  }
  return null;
}

export function socialIconUrl(key: SocialPlatformKey): string {
  return `/game/ui/social/${key}.svg`;
}
